import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import { auth, database } from "../../../utils/firebase";
import {onValue, ref, set} from "firebase/database";
import { useEffect, useState } from "react";
import {
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    CardOverflow,
    Chip,
    Select,
    Typography,
    Option
} from "@mui/joy";
import SuccessAlert from "../../reusable_elements/SuccessAlert";

const LeagueAccessApproval = () => {
    const navigate = useNavigate();
    const { appId } = useParams();
    const [userId, setUserId] = useState(null);
    const [appName, setAppName] = useState("");
    const [appLogoUrl, setAppLogoUrl] = useState("");
    const [appDescription, setAppDescription] = useState("");
    const [appUID, setAppUID] = useState("");
    const [appVerified, setAppVerified] = useState(false);
    const [leagueIds, setLeagueIds] = useState([]);
    const [leagues, setLeagues] = useState({});
    const [leagueId, setLeagueId] = useState(null);
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);

    useEffect(() => {
        if (auth.currentUser) {
            setUserId(auth.currentUser.uid);
        } else {
            console.error("User is not authenticated");
        }
    }, []);

    useEffect(() => {
        if (userId) {
            const userRef = ref(database, `people/${userId}`);
            onValue(userRef, (snapshot) => {
                const userData = snapshot.val();
                setLeagueIds(userData.leagues ? Object.keys(userData.leagues) : []);
            }, (error) => {
                console.error("Error fetching user data:", error);
            });
        }
    }, [userId]);

    useEffect(() => {
        leagueIds.forEach((leagueId) => {
            const leagueRef = ref(database, `leagues/${leagueId}`);
            onValue(leagueRef, (snapshot) => {
                setLeagues(prevLeagues => ({
                    ...prevLeagues,
                    [leagueId]: snapshot.val()
                }));
            }, (error) => {
                console.error("Error fetching league data:", error);
            });
        });
    }, [leagueIds]);

    useEffect(() => {
        const appRef = ref(database, `LID/apps/${appId}`);
        onValue(appRef, (snapshot) => {
            const appData = snapshot.val();
            setAppName(appData.appName);
            setAppLogoUrl(appData.appLogoUrl);
            setAppDescription(appData.appDescription);
            setAppUID(appData.appUID);
            setAppVerified(appData.verified);
        }, (error) => {
            console.error("Error fetching app data:", error);
        });
    }, [appId]);

    const handleConnect = () => {
        if (!leagueId) {
            console.error("No league selected");
            return;
        }

        const adminRef = ref(database, `leagues/${leagueId}/admins/${appUID}`);
        set(adminRef, {
            userId: appUID,
            role: 0,
            name: `${appName} (app)`,
            verified: appVerified,
            imageUrl: appLogoUrl,
            app: true
        }).then(() => {
            setSuccessAlertOpen(true);
            setTimeout(() => {
                navigate(`/utils/auth/LIDPost/${appId}/${leagueId}`);
            }, 500); // 10 seconds delay
        });
    }

    const handleCloseSuccessAlert = () => {
        setSuccessAlertOpen(false);
    };

    return (
        <div className="container" style={{ display: 'flex', justifyContent: 'center'}}>
            <Card sx={{ width: 320, maxWidth: '100%', boxShadow: 'lg' }}>
                <CardContent sx={{ alignItems: 'center', textAlign: 'center' }}>
                    <Typography component="p">{appName} (app) would like to connect to a league.</Typography>
                    <Avatar src={appLogoUrl} sx={{ '--Avatar-size': '4rem' }} />
                    {appVerified && (
                        <Chip
                            size="sm"
                            variant="soft"
                            color="primary"
                            sx={{
                                mt: -1,
                                mb: 1,
                                border: '3px solid',
                                borderColor: 'background.surface',
                            }}
                        >
                            Verified
                        </Chip>
                    )}
                    <Typography level="title-lg">{appName}</Typography>
                    <Typography level="body-sm" sx={{ maxWidth: '24ch' }}>
                        {appDescription}
                    </Typography>
                    <br/>
                    <Typography level="body-sm">
                        By connecting this app you are allowing {appName} to access and change your league's data.
                    </Typography>
                    <br/>
                    <Typography level="body-sm">
                        Please select the league you would like to allow {appName} to access.
                    </Typography>
                </CardContent>
                <CardOverflow sx={{ bgcolor: 'background.level1' }}>
                    <Select
                        value={leagueId}
                        onChange={(event, newValue) => setLeagueId(newValue)}
                        displayEmpty
                        sx={{ width: "100%", mt: 2 }}
                        placeholder="Select a league"
                    >
                        {leagueIds.map((id) => (
                            <Option value={id}>
                                {leagues[id]?.name || "Unknown League"}
                            </Option>
                        ))}
                    </Select>
                    <CardActions buttonFlex="1">
                        <Button onClick={() => handleConnect()}>Connect</Button>
                    </CardActions>
                </CardOverflow>
            </Card>
            {successAlertOpen &&
                <div>
                    <SuccessAlert message={"Successfully connected app"} onClose={handleCloseSuccessAlert} />
                </div>
            }
        </div>
    );
}

export default LeagueAccessApproval;